import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../network/ApiCall';
import LoadingSpinner from '../widgets/LoadingSpinner';
import { SYSTEM_UNION_CODE } from '../../config';

const IDPSettingsTable =() => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [idpSettingsByCountry, setIdpSettingsByCountry] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const unionCode = useMemo(() => (SYSTEM_UNION_CODE ? SYSTEM_UNION_CODE.toUpperCase() : null), []);

  useEffect(() => {
    let ignore = false;

    const loadTableData = async () => {
      if (!unionCode) {
        setCountries([]);
        setIdpSettingsByCountry({});
        setLoading(false);
        setTableLoading(false);
        return;
      }

      setLoading(true);
      setTableLoading(true);

      try {
        const [countriesResponse, idpSettingsResponse] = await Promise.all([
          axiosInstance.get(`/country/countriesByUnion/${unionCode}`),
          axiosInstance.get('/idpSettings'),
        ]);

        if (ignore) {
          return;
        }

        const countryRows = Array.isArray(countriesResponse?.data) ? countriesResponse.data : [];
        const idpSettingsRows = Array.isArray(idpSettingsResponse?.data) ? idpSettingsResponse.data : [];

        const nextIdpSettingsByCountry = idpSettingsRows.reduce((accumulator, row) => {
          const countryCode = row?.countryCode?.toUpperCase?.();
          if (countryCode) {
            accumulator[countryCode] = row;
          }
          return accumulator;
        }, {});

        setCountries(countryRows);
        setIdpSettingsByCountry(nextIdpSettingsByCountry);
      } catch (error) {
        console.error('ERROR LOADING UNION IDP SETTINGS TABLE', error);
        if (!ignore) {
          setCountries([]);
          setIdpSettingsByCountry({});
        }
      } finally {
        if (!ignore) {
          setLoading(false);
          setTableLoading(false);
        }
      }
    };

    loadTableData();

    return () => {
      ignore = true;
    };
  }, [unionCode]);

  const tableData = useMemo(() => (
    countries.map((country) => {
      const isoCode2 = country?.isoCode2?.toUpperCase?.() ?? '';
      const idpSettings = isoCode2 ? idpSettingsByCountry[isoCode2] : undefined;

      return {
        ...country,
        isoCode2,
        idpSettings,
        idpType: idpSettings?.idpType ?? null,
        enabled: typeof idpSettings?.enabled === 'boolean' ? idpSettings.enabled : null,
      };
    })
  ), [countries, idpSettingsByCountry]);

  const idpTypeLabels = useMemo(() => ({
    OIDC_SECRET: t('idpSettings:optOAuth2'),
    OIDC_PKCE: t('idpSettings:optOidc'),
    SAML: t('idpSettings:optSaml'),
  }), [t]);

  const columns = useMemo(() => ([
    {
      name: t('idpSettings:lblCountry'),
      selector: (row) => row.isoCode2 ?? '',
      sortable: true,
      cell: (row) => {
        const countryCode = row.isoCode2 ?? '';
        const countryName = countryCode ? t(`countries:${countryCode}`, { defaultValue: countryCode }) : t('common:lblNotSpecified');
        const label = countryCode ? `${countryCode} - ${countryName}` : countryName;

        if (!countryCode) {
          return <span>{label}</span>;
        }

        return (
          <Link to={`/app/idpSettings/${countryCode}`} className="btn btn-link p-0">
            {label}
          </Link>
        );
      },
    },
    {
      name: t('idpSettings:lblIdpType'),
      selector: (row) => row.idpType ?? '',
      sortable: true,
      cell: (row) => {
        if (!row.idpType) {
          return t('common:lblNotSpecified');
        }

        const normalized = row.idpType.toUpperCase();
        return idpTypeLabels[normalized] ?? normalized;
      },
    },
    {
      name: t('idpSettings:lblSSOEnabled'),
      selector: (row) => {
        if (row.enabled === true) {
          return 'true';
        }
        if (row.enabled === false) {
          return 'false';
        }
        return '';
      },
      sortable: true,
      cell: (row) => {
        if (row.enabled === true) {
          return t('common:lblYes');
        }
        if (row.enabled === false) {
          return t('common:lblNo');
        }
        return t('common:lblNotSpecified');
      },
    },
  ]), [idpTypeLabels, t]);

  return (

        loading ? (
          <LoadingSpinner />
        ) : (
          <DataTable
            noHeader
            columns={columns}
            data={tableData}
            striped
            progressPending={tableLoading}
          />
        )
  );
};

export default IDPSettingsTable;
